import React, { CSSProperties, useEffect, useState } from 'react';
import {
  OpenWeather,
  useOpenWeatherDetailQuery,
  useOpenWeatherDetailUpdateSubscription,
} from '../../../../types/generated-types';
import BorderedSection from '../../shared/borderedSection';
import { DisplayValue } from '../shared-ui';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';

import { convertToTempUnits } from '../util';
// import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import { PanelFrame } from '../shared-ui/panel-frame';
import DeviceLoadingSkeleton from '../shared-ui/deviceLoadingSkeleton';
import {
  Fahrenheit,
  TemperatureUnit,
} from '../../../system/models/temperatureUnits';
import { DeviceInformation } from '../shared-ui/device-info';
import moment from 'moment/moment';

const locationLabel = (device: Partial<OpenWeather>) => {
  const location = device.meta?.location;
  return location ? `Currently in ${location}` : undefined;
};

const SimpleOpenWeather = ({
  device,
  embedded,
  style,
  preferredUnits = Fahrenheit,
}: {
  device: Partial<OpenWeather>;
  embedded: boolean;
  style: CSSProperties;
  preferredUnits: TemperatureUnit;
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flex: '1 1 auto',
        width: '100%',
        ...style,
      }}
    >
      <DisplayValue
        value={convertToTempUnits(device?.temperature?.value, preferredUnits)}
        units={preferredUnits}
        valueSize={!embedded ? '30px' : '16px'}
        unitsSize={!embedded ? '15px' : '12px'}
        style={{ width: '100%', textAlign: 'center' }}
        label={locationLabel(device)}
        labelStyle={{ fontStyle: 'normal' }}
      />
    </div>
  );
};

export function OpenWeatherComponent({
  deviceId,
  label,
  embedded = true,
  style = {},
  preferredUnits = Fahrenheit,
}: {
  deviceId: string;
  label: string;
  embedded?: boolean;
  style?: CSSProperties | undefined;
  preferredUnits?: TemperatureUnit;
}) {
  const [device, setDevice] = useState<Partial<OpenWeather>>();

  const {
    data,
    // TODO: Loading_error: handle loading/errors for open weather detail queries.
    // loading,
    // error,
  } = useOpenWeatherDetailQuery({
    variables: { id: deviceId },
  });
  const [deviceInfo, setDeviceInfo] = useState<DeviceInformation[]>([]);

  useOpenWeatherDetailUpdateSubscription({
    variables: { ids: [deviceId] },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  useEffect(() => {
    if (data?.openWeatherById) {
      setDevice({
        ...data.openWeatherById,
        name: data.openWeatherById.name ?? label,
      } as Partial<OpenWeather>);
    }
  }, [data, label]);

  useEffect(() => {
    if (device) {
      const panelInfo: DeviceInformation = {
        Panel: device.panel?.displayName ?? 'Unnamed',
        'Panel ID': device.panelId ?? 'Unidentified',
        'Panel Type': device.panel?.type ?? 'Unknown',
        Source: device.source?.name ?? 'Unnamed source',
      };

      const devicesInfo: DeviceInformation[] = [
        {
          'Device ID': device.deviceId ?? device._id ?? 'Unidentified',
          Name: device.name ?? 'Unnamed',
          Type: device.typeDisplayName ?? 'Unknown',
          'Last Updated': moment(device.timestamp).fromNow(),
        },
      ];

      setDeviceInfo([panelInfo, ...devicesInfo]);
    }
  }, [device]);

  return device ? (
    embedded ? (
      <BorderedSection
        title={label}
        labelStyle={{ fontSize: '10px' }}
        style={{
          height: '45px',
          width: '100%',
        }}
        ccStyle={{
          padding: '6px',
          width: '100%',
        }}
      >
        <SimpleOpenWeather
          device={device}
          embedded={true}
          style={style}
          preferredUnits={preferredUnits}
        />
      </BorderedSection>
    ) : (
      <PanelFrame
        iconType="ic:baseline-thermostat"
        panel={device.panel}
        infoEntries={deviceInfo}
        editingEnabled={false}
      >
        <SimpleOpenWeather
          device={device}
          embedded={false}
          style={style}
          preferredUnits={preferredUnits}
        />
      </PanelFrame>
    )
  ) : (
    <DeviceLoadingSkeleton />
  );
}
